import {
  SurfSyncEngine,
  type SurfDeck,
  type SurfDeckRecord,
  type SurfLibrarySnapshot,
  type SurfSyncEngineStatus,
} from "@vida/surf-core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { decodeJwtPayload, getIdToken } from "../auth";
import { getAppsyncEndpoint } from "../config";

type SurfLibraryContextValue = {
  decks: SurfDeckRecord[];
  status: SurfSyncEngineStatus;
  error: string | null;
  ready: boolean;
  createDeck: (title?: string, projectId?: string) => Promise<SurfDeckRecord>;
  saveDeck: (deck: SurfDeck, documentId: string) => Promise<SurfDeckRecord>;
  getDeck: (documentId: string) => SurfDeckRecord | undefined;
  renameDeck: (documentId: string, title: string) => Promise<SurfDeckRecord>;
  deleteDeck: (documentId: string) => Promise<void>;
  duplicateDeck: (documentId: string) => Promise<SurfDeckRecord>;
  refresh: () => Promise<SurfLibrarySnapshot>;
};

const SurfLibraryContext = createContext<SurfLibraryContextValue | null>(null);

export function SurfLibraryProvider({ children }: { children: ReactNode }) {
  const engineRef = useRef<SurfSyncEngine | null>(null);
  const [snapshot, setSnapshot] = useState<SurfLibrarySnapshot>({ decks: [], lastSyncedAt: "" });
  const [status, setStatus] = useState<SurfSyncEngineStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const engine = new SurfSyncEngine({
      endpoint: getAppsyncEndpoint(),
      getIdToken,
      decodeJwt: decodeJwtPayload,
      onSnapshot: setSnapshot,
      onStatus: setStatus,
      onError: (msg) => setError(msg),
    });
    engineRef.current = engine;

    void engine
      .start()
      .then(() => setReady(true))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      void engine.stop();
      engineRef.current = null;
    };
  }, []);

  const withEngine = useCallback(<T,>(fn: (engine: SurfSyncEngine) => Promise<T>) => {
    const engine = engineRef.current;
    if (!engine) return Promise.reject(new Error("Sync non pronto"));
    setError(null);
    return fn(engine);
  }, []);

  const value = useMemo<SurfLibraryContextValue>(
    () => ({
      decks: snapshot.decks,
      status,
      error,
      ready,
      createDeck: (title?: string, projectId?: string) =>
        withEngine((e) => e.createDeck(title, projectId)),
      saveDeck: (deck: SurfDeck, documentId: string) =>
        withEngine((e) => {
          const current = snapshot.decks.find((d) => d.documentId === documentId);
          return e.saveDeck(deck, current?.version);
        }),
      getDeck: (documentId: string) => snapshot.decks.find((d) => d.documentId === documentId),
      renameDeck: (documentId: string, title: string) =>
        withEngine((e) => e.renameDeck(documentId, title)),
      deleteDeck: (documentId: string) => withEngine((e) => e.deleteDeck(documentId)),
      duplicateDeck: (documentId: string) => withEngine((e) => e.duplicateDeck(documentId)),
      refresh: () => withEngine((e) => e.pullOnce()),
    }),
    [snapshot.decks, status, error, ready, withEngine],
  );

  return <SurfLibraryContext.Provider value={value}>{children}</SurfLibraryContext.Provider>;
}

export function useSurfLibrary(): SurfLibraryContextValue {
  const ctx = useContext(SurfLibraryContext);
  if (!ctx) throw new Error("useSurfLibrary richiede SurfLibraryProvider");
  return ctx;
}

export type { SurfDeckRecord };
